import { useState, useEffect } from 'react';
import {
  ClipboardCheck,
  Loader2,
  AlertCircle,
  MapPin,
  CheckCircle2,
  MessageSquareWarning,
  Bed,
  Image as ImageIcon,
  Calendar,
  ChevronRight,
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  Save,
} from 'lucide-react';
import { PropertyReviewStatus } from '@pms/shared';
import {
  usePlatformReviewQueue,
  usePlatformPropertyDetail,
  useApproveProperty,
  useRequestPropertyChanges,
  useUpdatePropertyImages,
} from '@/hooks/api/usePlatform';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { ReviewStatusBadge } from '@/components/ui/ReviewStatusBadge';
import { SlideOver } from '@/components/ui/SlideOver';
import { Button } from '@/components/ui/Button';
import { extractErrorMessage } from '@/lib/error';
import { cn } from '@/lib/utils';

const STATUS_TABS: { value: PropertyReviewStatus; label: string }[] = [
  { value: PropertyReviewStatus.PENDING_REVIEW, label: 'Pending Review' },
  { value: PropertyReviewStatus.CHANGES_REQUESTED, label: 'Changes Requested' },
  { value: PropertyReviewStatus.APPROVED, label: 'Approved' },
  { value: PropertyReviewStatus.DRAFT, label: 'Draft' },
];

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ── Images editor (TASK-60) ────────────────────────────────────────────────────

function ImagesEditor({
  propertyId,
  propertyName,
  initialImages,
}: {
  propertyId: string;
  propertyName: string;
  initialImages: string[];
}) {
  const [images, setImages] = useState<string[]>(initialImages);
  const [newUrl, setNewUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const update = useUpdatePropertyImages();

  // Reset local draft whenever the server detail (and thus images) changes.
  useEffect(() => {
    setImages(initialImages);
  }, [initialImages]);

  const dirty =
    images.length !== initialImages.length ||
    images.some((url, i) => url !== initialImages[i]);

  function handleAdd() {
    const url = newUrl.trim();
    if (!url) return;
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        throw new Error('protocol');
      }
    } catch {
      setError('Enter a valid http(s) URL.');
      return;
    }
    if (images.length >= 20) {
      setError('A listing may have at most 20 images.');
      return;
    }
    setError(null);
    setImages((prev) => [...prev, url]);
    setNewUrl('');
  }

  function handleRemove(idx: number) {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  }

  function move(idx: number, dir: -1 | 1) {
    setImages((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target]!, next[idx]!];
      return next;
    });
  }

  function handleSave() {
    setError(null);
    update.mutate(
      { id: propertyId, images },
      { onError: (err) => setError(extractErrorMessage(err)) },
    );
  }

  return (
    <section>
      <h4 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 mb-2">
        <ImageIcon size={14} className="text-gray-400" aria-hidden="true" /> Photos ({images.length})
      </h4>

      {images.length === 0 ? (
        <p className="text-sm text-gray-400 mb-3">No photos yet. Add an image URL below.</p>
      ) : (
        <ul className="space-y-2 mb-3">
          {images.map((src, i) => (
            <li
              key={`${src}-${i}`}
              className="flex items-center gap-3 rounded-lg border border-gray-200 p-2"
            >
              <img
                src={src}
                alt={`${propertyName} photo ${i + 1}`}
                loading="lazy"
                className="w-16 h-16 object-cover rounded-md border border-gray-200 bg-gray-100 shrink-0"
              />
              <span className="flex-1 min-w-0 text-xs text-gray-500 break-all line-clamp-2">
                {src}
              </span>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  aria-label={`Move photo ${i + 1} up`}
                  className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <ArrowUp size={14} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  disabled={i === images.length - 1}
                  aria-label={`Move photo ${i + 1} down`}
                  className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <ArrowDown size={14} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => handleRemove(i)}
                  aria-label={`Remove photo ${i + 1}`}
                  className="p-1.5 rounded-md text-red-400 hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Add URL row */}
      <div className="flex items-center gap-2">
        <input
          type="url"
          value={newUrl}
          onChange={(e) => setNewUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
          placeholder="https://example.com/photo.jpg"
          aria-label="New image URL"
          className={cn(
            'flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800',
            'placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500',
          )}
        />
        <Button variant="secondary" onClick={handleAdd} disabled={!newUrl.trim()}>
          <Plus size={16} aria-hidden="true" />
          Add
        </Button>
      </div>

      {error && (
        <p className="mt-2 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      <div className="mt-3 flex items-center justify-end gap-3">
        {update.isSuccess && !dirty && (
          <span className="text-xs text-green-600 flex items-center gap-1">
            <CheckCircle2 size={14} aria-hidden="true" /> Saved
          </span>
        )}
        <Button
          variant="primary"
          onClick={handleSave}
          isLoading={update.isPending}
          disabled={!dirty || update.isPending}
        >
          <Save size={16} aria-hidden="true" />
          Save photos
        </Button>
      </div>
    </section>
  );
}

// ── Review detail panel ────────────────────────────────────────────────────────

function ReviewDetail({ propertyId, onClose }: { propertyId: string; onClose: () => void }) {
  const { data, isLoading, isError, refetch } = usePlatformPropertyDetail(propertyId);
  const approve = useApproveProperty();
  const requestChanges = useRequestPropertyChanges();
  const [feedback, setFeedback] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  function handleApprove() {
    setActionError(null);
    approve.mutate(propertyId, {
      onSuccess: onClose,
      onError: (err) => setActionError(extractErrorMessage(err)),
    });
  }

  function handleRequestChanges() {
    if (feedback.trim().length === 0) return;
    setActionError(null);
    requestChanges.mutate(
      { id: propertyId, feedback: feedback.trim() },
      {
        onSuccess: onClose,
        onError: (err) => setActionError(extractErrorMessage(err)),
      },
    );
  }

  const isBusy = approve.isPending || requestChanges.isPending;

  return (
    <SlideOver isOpen onClose={onClose} title="Review property" width="max-w-2xl">
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin text-brand-600" aria-hidden="true" />
        </div>
      ) : isError || !data ? (
        <div className="py-16 text-center">
          <AlertCircle size={24} className="mx-auto text-red-500 mb-3" aria-hidden="true" />
          <p className="text-sm text-red-700 mb-4">Unable to load this property.</p>
          <Button variant="secondary" onClick={() => void refetch()}>Retry</Button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Header */}
          <div>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{data.name}</h3>
                <p className="text-sm text-gray-500">{data.operatorName}</p>
              </div>
              <ReviewStatusBadge status={data.reviewStatus} />
            </div>
            {data.tagLine && <p className="mt-2 text-sm italic text-gray-500">"{data.tagLine}"</p>}
            <div className="mt-3 flex items-center gap-1.5 text-sm text-gray-600">
              <MapPin size={14} className="text-gray-400" aria-hidden="true" />
              {data.address}, {data.city}{data.state ? `, ${data.state}` : ''}, {data.country}
            </div>
          </div>

          {/* Existing feedback (if changes were previously requested) */}
          {data.reviewFeedback && (
            <div className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-3">
              <p className="text-xs font-semibold text-orange-800 uppercase tracking-wide mb-1">Last feedback to operator</p>
              <p className="text-sm text-orange-900 whitespace-pre-wrap">{data.reviewFeedback}</p>
            </div>
          )}

          {/* Submitted / reviewed timeline */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-gray-200 px-4 py-3">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                <Calendar size={12} aria-hidden="true" /> Submitted
              </div>
              <p className="text-sm text-gray-700">{formatDate(data.submittedAt)}</p>
            </div>
            <div className="rounded-lg border border-gray-200 px-4 py-3">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                <Calendar size={12} aria-hidden="true" /> Last reviewed
              </div>
              <p className="text-sm text-gray-700">{formatDate(data.reviewedAt)}</p>
            </div>
          </div>

          {/* Description */}
          {data.description && (
            <section>
              <h4 className="text-sm font-semibold text-gray-900 mb-1.5">Description</h4>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{data.description}</p>
            </section>
          )}

          {/* Photos — editable (TASK-60) */}
          <ImagesEditor
            propertyId={data.id}
            propertyName={data.name}
            initialImages={data.images}
          />

          {/* Units */}
          <section>
            <h4 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 mb-2">
              <Bed size={14} className="text-gray-400" aria-hidden="true" /> Units ({data.units.length})
            </h4>
            {data.units.length === 0 ? (
              <p className="text-sm text-gray-400">No units defined.</p>
            ) : (
              <div className="space-y-2">
                {data.units.map((u) => (
                  <div key={u.id} className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-2.5">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{u.name}</p>
                      <p className="text-xs text-gray-500">{u.type} · up to {u.capacity} guests</p>
                    </div>
                    <p className="text-sm font-semibold text-gray-700">
                      {data.currency} {Number(u.basePrice).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Policies */}
          <section>
            <h4 className="text-sm font-semibold text-gray-900 mb-2">Policies</h4>
            <dl className="rounded-lg border border-gray-200 divide-y divide-gray-100 text-sm">
              <div className="flex justify-between px-4 py-2.5">
                <dt className="text-gray-500">Check-in / Check-out</dt>
                <dd className="text-gray-800 font-medium">{data.policies.checkInTime} / {data.policies.checkOutTime}</dd>
              </div>
              <div className="flex justify-between px-4 py-2.5">
                <dt className="text-gray-500">Cancellation</dt>
                <dd className="text-gray-800 font-medium">{data.policies.cancellationPolicy}</dd>
              </div>
              <div className="flex justify-between px-4 py-2.5">
                <dt className="text-gray-500">Pets</dt>
                <dd className="text-gray-800 font-medium">{data.policies.petPolicy}</dd>
              </div>
              <div className="flex justify-between px-4 py-2.5">
                <dt className="text-gray-500">Smoking</dt>
                <dd className="text-gray-800 font-medium">{data.policies.smokingPolicy}</dd>
              </div>
              {data.policies.houseRules && (
                <div className="px-4 py-2.5">
                  <dt className="text-gray-500 mb-1">House rules</dt>
                  <dd className="text-gray-800 whitespace-pre-wrap">{data.policies.houseRules}</dd>
                </div>
              )}
            </dl>
          </section>

          {/* Amenities */}
          {data.amenities.length > 0 && (
            <section>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Amenities</h4>
              <div className="flex flex-wrap gap-1.5">
                {data.amenities.map((a) => (
                  <span key={a} className="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-medium text-brand-700">
                    {a}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Action error */}
          {actionError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
              {actionError}
            </p>
          )}

          {/* Request changes feedback box */}
          {showFeedback && (
            <div>
              <label htmlFor="review-feedback" className="block text-sm font-medium text-gray-700 mb-1.5">
                Feedback for the operator <span className="text-red-500" aria-hidden="true">*</span>
              </label>
              <textarea
                id="review-feedback"
                rows={4}
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Describe what needs to change before this listing can be approved…"
                className={cn(
                  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800',
                  'placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none',
                )}
              />
            </div>
          )}
        </div>
      )}

      {/* Sticky action bar */}
      {data && (
        <div className="sticky bottom-0 -mx-5 -mb-4 mt-6 flex items-center justify-end gap-3 border-t border-gray-100 bg-white px-5 py-3">
          {!showFeedback ? (
            <>
              <Button
                variant="secondary"
                onClick={() => { setShowFeedback(true); setActionError(null); }}
                disabled={isBusy}
              >
                <MessageSquareWarning size={16} aria-hidden="true" />
                Request changes
              </Button>
              <Button
                variant="primary"
                onClick={handleApprove}
                isLoading={approve.isPending}
                disabled={isBusy}
              >
                <CheckCircle2 size={16} aria-hidden="true" />
                Approve
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => { setShowFeedback(false); setFeedback(''); }} disabled={isBusy}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleRequestChanges}
                isLoading={requestChanges.isPending}
                disabled={isBusy || feedback.trim().length === 0}
              >
                Send feedback
              </Button>
            </>
          )}
        </div>
      )}
    </SlideOver>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function AdminReviewsPage() {
  const [status, setStatus] = useState<PropertyReviewStatus>(PropertyReviewStatus.PENDING_REVIEW);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data, isLoading, isError, refetch } = usePlatformReviewQueue(status);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Review Queue"
        description="Approve operator listings for the public storefront, or send back feedback."
      />

      {/* Status tabs */}
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Review status filter">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={status === tab.value}
            onClick={() => setStatus(tab.value)}
            className={cn(
              'px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
              status === tab.value
                ? 'bg-brand-600 text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-brand-600" aria-hidden="true" />
            <span className="ml-2 text-sm text-gray-500">Loading queue…</span>
          </div>
        ) : isError ? (
          <div className="py-16 text-center">
            <AlertCircle size={24} className="mx-auto text-red-500 mb-3" aria-hidden="true" />
            <p className="text-sm text-red-700 mb-4">Unable to load the review queue.</p>
            <Button variant="secondary" onClick={() => void refetch()}>Retry</Button>
          </div>
        ) : !data || data.length === 0 ? (
          <EmptyState
            icon={ClipboardCheck}
            title="Nothing here"
            description={`No properties with status "${STATUS_TABS.find((t) => t.value === status)?.label}".`}
          />
        ) : (
          <ul className="divide-y divide-gray-100">
            {data.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(p.id)}
                  className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500"
                >
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-brand-100 shrink-0" aria-hidden="true">
                    <ClipboardCheck size={18} className="text-brand-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{p.name}</p>
                    <p className="text-sm text-gray-500 truncate">
                      {p.operatorName} · {p.city}, {p.country}
                    </p>
                  </div>
                  <ReviewStatusBadge status={p.reviewStatus} />
                  <ChevronRight size={16} className="text-gray-300 shrink-0" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selectedId && <ReviewDetail propertyId={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}
